import { useState, useEffect, type FormEvent } from 'react';
import {
    fetchAllDepartments, createDepartment, updateDepartment,
    type DepartmentRecord,
} from '../../services/staffApi';
import type { ApiError } from '../../services/api';
import './Departments.css';

/* ── Types ─────────────────────────────────────────── */

interface FormErrors {
    name?: string;
}

/* ── Helpers ───────────────────────────────────────── */

function formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ── Component ─────────────────────────────────────── */

type ViewMode = 'list' | 'create' | 'edit';

export default function Departments() {
    /* --- data state --- */
    const [departments, setDepartments] = useState<DepartmentRecord[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);

    /* --- view state --- */
    const [view, setView] = useState<ViewMode>('list');
    const [successMsg, setSuccessMsg] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [fetchError, setFetchError] = useState('');

    /* --- load on mount --- */
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                setFetchError('');
                const data = await fetchAllDepartments();
                setDepartments(data);
            } catch (err: unknown) {
                const message = err && typeof err === 'object' && 'message' in err
                    ? (err as { message: string }).message
                    : 'Failed to load departments.';
                setFetchError(message);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    /* --- form state --- */
    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);

    /* --- UI state --- */
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    /* ---- Navigation ---- */
    const resetForm = () => {
        setName('');
        setIsActive(true);
        setEditingId(null);
        setErrors({});
    };

    const goToCreate = () => { resetForm(); setView('create'); };

    const goToEdit = (dept: DepartmentRecord) => {
        setName(dept.name);
        setIsActive(dept.is_active);
        setEditingId(dept.id);
        setErrors({});
        setView('edit');
    };

    const goToList = () => { resetForm(); setView('list'); };

    /* ---- Validation ---- */
    const validate = (): boolean => {
        const newErrors: FormErrors = {};
        if (!name.trim()) {
            newErrors.name = 'Department name is required.';
        } else if (name.trim().length > 100) {
            newErrors.name = 'Name must be 100 characters or fewer.';
        } else {
            const duplicate = departments.find(
                (d) => d.name.toLowerCase() === name.trim().toLowerCase() && d.id !== editingId
            );
            if (duplicate) newErrors.name = 'A department with this name already exists.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ---- Submit ---- */
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        setIsSubmitting(true);

        const payload = { name: name.trim(), is_active: isActive };

        try {
            if (view === 'edit' && editingId) {
                await updateDepartment(editingId, payload);
            } else {
                await createDepartment(payload);
            }
            const data = await fetchAllDepartments();
            setDepartments(data);
            setSuccessMsg(view === 'edit' ? 'Department updated successfully!' : 'Department created successfully!');
            resetForm();
            setView('list');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            if (apiErr.errors?.name) {
                setErrors({ name: apiErr.errors.name[0] });
            } else {
                setErrors({ name: apiErr.message || 'Failed to save department.' });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ────────────────── Render: List View ────────────────── */
    if (view === 'list') {
        return (
            <div className="depts">
                {successMsg && (
                    <div className="depts__toast">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        <span>{successMsg}</span>
                        <button className="depts__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                    </div>
                )}

                <div className="depts__header">
                    <div className="depts__header-text">
                        <h1 className="depts__title">Departments</h1>
                        <p className="depts__subtitle">Manage staff departments for your organisation.</p>
                    </div>
                    <button className="depts__add-btn" onClick={goToCreate}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add Department
                    </button>
                </div>

                {fetchError && (
                    <div className="depts__error-banner">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {fetchError}
                    </div>
                )}

                <div className="depts__table-wrap">
                    {isLoading ? (
                        <div className="depts__loading">
                            <svg className="depts__spinner" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" strokeWidth="3" />
                            </svg>
                            <span>Loading…</span>
                        </div>
                    ) : departments.length === 0 ? (
                        <div className="depts__empty">
                            <div className="depts__empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                            </div>
                            <h3 className="depts__empty-title">No departments yet</h3>
                            <p className="depts__empty-text">Create your first department to get started.</p>
                        </div>
                    ) : (
                        <table className="depts__table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th>Created</th>
                                    <th className="depts__col-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {departments.map((d) => (
                                    <tr key={d.id}>
                                        <td className="depts__col-name">{d.name}</td>
                                        <td>
                                            {d.is_active ? (
                                                <span className="depts__badge depts__badge--active">Active</span>
                                            ) : (
                                                <span className="depts__badge depts__badge--inactive">Inactive</span>
                                            )}
                                        </td>
                                        <td>{formatDate(d.created_at)}</td>
                                        <td className="depts__col-actions">
                                            <button className="depts__edit-btn" onClick={() => goToEdit(d)}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        );
    }

    /* ────────────────── Render: Create / Edit Form ────────────────── */
    return (
        <div className="depts">
            <div className="depts__form-header">
                <button className="depts__back-btn" onClick={goToList} title="Back to list">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
                <h1 className="depts__form-title">
                    {view === 'edit' ? 'Edit Department' : 'New Department'}
                </h1>
            </div>

            <div className="depts__card">
                <form className="depts__form" onSubmit={handleSubmit} noValidate>

                    {/* Name */}
                    <div className="depts__field">
                        <label className="depts__label depts__label--required" htmlFor="deptName">
                            Department Name
                        </label>
                        <div className="depts__input-wrap">
                            <span className="depts__input-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                </svg>
                            </span>
                            <input
                                className={`depts__input ${errors.name ? 'depts__input--error' : ''}`}
                                type="text"
                                id="deptName"
                                placeholder="e.g. Science, Administration"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                                }}
                                maxLength={100}
                                autoFocus
                            />
                        </div>
                        {errors.name ? (
                            <p className="depts__error">{errors.name}</p>
                        ) : (
                            <p className="depts__helper">Enter a unique name for this department.</p>
                        )}
                    </div>

                    <hr className="depts__divider" />

                    {/* Active toggle */}
                    <div className="depts__field">
                        <div className="depts__toggle-row">
                            <div className="depts__toggle-info">
                                <span className="depts__toggle-label">Active</span>
                                <span className="depts__toggle-desc">Inactive departments are hidden from staff assignment dropdowns.</span>
                            </div>
                            <label className="depts__toggle">
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                />
                                <span className="depts__toggle-track" />
                            </label>
                        </div>
                    </div>

                    <hr className="depts__divider" />

                    {/* Actions */}
                    <div className="depts__actions">
                        <button className="depts__submit" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <svg className="depts__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" strokeWidth="3" />
                                    </svg>
                                    {view === 'edit' ? 'Updating…' : 'Creating…'}
                                </>
                            ) : (
                                view === 'edit' ? 'Update Department' : 'Create Department'
                            )}
                        </button>
                        <button className="depts__cancel" type="button" onClick={goToList} disabled={isSubmitting}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
